import { useState, useRef, useEffect, useCallback } from "react";
import gsap from "gsap";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EllipsisHorizontalIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";

// Feature imports
import {
  AnimatedNumber,
  ArchiveStatusSection,
  CameraStatsSection,
  DateFilter,
  DetectionStatsSection,
  EditorSendsTab,
  ImageSearch,
  ModelQualityTab,
  PipelineFlow,
  PipelineTimeline,
  ServiceCard,
  SystemTab,
  TrainingTab,
} from "../features/statistics/components";
import { useStatisticsData } from "../features/statistics/hooks";
import { SERVICE_ORDER, STATS_TABS } from "../features/statistics/constants";
import { formatMs } from "../features/statistics/utils";

export default function Statistics({ onNavigate, onTopBadge, showRain }) {
  const [expandedService, setExpandedService] = useState(null);
  const [activeTab, setActiveTab] = useState("pipeline");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Refs for GSAP animations
  const summaryCardsRef = useRef([]);
  const summaryAnimDone = useRef(false);

  // Load all statistics data via hook
  const {
    stats,
    metricsStats,
    rate,
    loading,
    metricsLoading,
    derivedStats,
    totalPipelineMs,
    cameraStats,
    archiveStatus,
    detectionStats,
    editorSendsStats,
    aiQualityStats,
    trainingStats,
    systemStatus,
    selectedImage,
    setSelectedImage,
    imageMeta,
  } = useStatisticsData({ activeTab, fromDate, toDate, onTopBadge });

  // Reusable GSAP animation for card grids
  const animateCards = useCallback((refs) => {
    const elements = refs.current?.filter(Boolean);
    if (!elements?.length) return;
    gsap.fromTo(
      elements,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: "power2.out" }
    );
  }, []);

  // Animate summary cards on initial load only
  useEffect(() => {
    if (!loading && stats && !summaryAnimDone.current) {
      animateCards(summaryCardsRef);
      summaryAnimDone.current = true;
    }
  }, [loading, stats, animateCards]);

  if (loading) {
    return (
      <div className={`page page-statistics ${showRain ? "matrix-mode" : ""}`}>
        <div className="stats-loading">
          <div className="loading-matrix">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page page-statistics ${showRain ? "matrix-mode" : ""}`}>
      {/* Matrix background effect */}
      {showRain && <div className="matrix-grid-overlay" />}

      {/* Header */}
      <div className="stats-header">
        <h1 className={showRain ? "matrix-title" : ""}>System Analytics</h1>
        <div className="stats-tabs">
          {STATS_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`stats-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className={`stats-summary ${showRain ? "matrix-summary" : ""}`}>
        <div className="summary-card total" ref={(el) => (summaryCardsRef.current[0] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <HashtagIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={derivedStats.total || 0} />
          </div>
          <div className="summary-label">Total Images</div>
        </div>
        <div className="summary-card done" ref={(el) => (summaryCardsRef.current[1] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <CheckCircleIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={derivedStats.done || 0} />
          </div>
          <div className="summary-label">Completed</div>
        </div>
        <div className="summary-card processing" ref={(el) => (summaryCardsRef.current[2] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <EllipsisHorizontalIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={derivedStats.processing || 0} />
          </div>
          <div className="summary-label">Processing</div>
        </div>
        <div className="summary-card attention" ref={(el) => (summaryCardsRef.current[3] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <ExclamationTriangleIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={derivedStats.attention || 0} />
          </div>
          <div className="summary-label">Attention</div>
        </div>
        <div className="summary-card rate" ref={(el) => (summaryCardsRef.current[4] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">/m</div>
          <div className="summary-value">{rate?.per_min?.toFixed(1) || "-"}</div>
          <div className="summary-label">Images/min</div>
        </div>
        <div className="summary-card pipeline-time" ref={(el) => (summaryCardsRef.current[5] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">ms</div>
          <div className="summary-value">{formatMs(totalPipelineMs)}</div>
          <div className="summary-label">Avg Pipeline</div>
        </div>
      </div>

      {/* Performance stats tab */}
      {activeTab === "pipeline" && (
        <div className={`stats-content ${showRain ? "matrix-content" : ""}`}>
          {/* Loading overlay for date filtering */}
          {metricsLoading && (
            <div className="metrics-loading-overlay">
              <div className="loading-spinner">
                <div className="spinner-ring" />
                <div className="spinner-ring" />
                <div className="spinner-ring" />
              </div>
              <span className="loading-text">Loading metrics...</span>
            </div>
          )}
          <div className="stats-section services-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Service Performance
              </h2>
              <p className="stats-hint">Click a service to view estimated step breakdown</p>
            </div>
            <DateFilter
              fromDate={fromDate}
              toDate={toDate}
              onChange={(from, to) => {
                setFromDate(from);
                setToDate(to);
              }}
              filteredCount={metricsStats?.count}
              showRain={showRain}
            />
            <div className="services-grid">
              {SERVICE_ORDER.map((service) => (
                <ServiceCard
                  key={service}
                  service={service}
                  stats={metricsStats}
                  isExpanded={expandedService === service}
                  onClick={() => setExpandedService(expandedService === service ? null : service)}
                  showRain={showRain}
                />
              ))}
            </div>
          </div>

          <PipelineFlow
            metricsStats={metricsStats}
            totalPipelineMs={totalPipelineMs}
            showRain={showRain}
          />
        </div>
      )}

      {/* Timeline tab */}
      {activeTab === "timeline" && (
        <div className={`stats-content timeline-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Image Pipeline Timeline
              </h2>
              <p className="stats-hint">Search for an image to trace its journey through the system</p>
            </div>
            <ImageSearch onSelect={setSelectedImage} showRain={showRain} />
            <PipelineTimeline
              imageId={selectedImage?.image_id}
              imageMeta={imageMeta}
              showRain={showRain}
            />
          </div>
        </div>
      )}

      {/* Camera tab */}
      {activeTab === "camera" && (
        <div className={`stats-content camera-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Camera & EXIF Statistics
              </h2>
              <p className="stats-hint">Equipment and shooting parameters from your photo collection</p>
            </div>
            <CameraStatsSection cameraStats={cameraStats} showRain={showRain} />
          </div>
        </div>
      )}

      {/* Archive tab */}
      {activeTab === "archive" && (
        <div className={`stats-content archive-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Archive Status
              </h2>
              <p className="stats-hint">Track archive jobs and storage operations</p>
            </div>
            <ArchiveStatusSection archiveStatus={archiveStatus} showRain={showRain} />
          </div>
        </div>
      )}

      {/* Detection tab */}
      {activeTab === "detection" && (
        <div className={`stats-content detection-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Detection & Crop Quality
              </h2>
              <p className="stats-hint">Analyze detection accuracy, crop types, and manual adjustment rates</p>
            </div>
            <DetectionStatsSection detectionStats={detectionStats} showRain={showRain} />
          </div>
        </div>
      )}

      {/* Editor Sends tab */}
      {activeTab === "editor_sends" && (
        <div className={`stats-content editor-sends-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Editor Sends
              </h2>
              <p className="stats-hint">Images sent to external editor for manual processing</p>
            </div>
            <EditorSendsTab editorSendsStats={editorSendsStats} showRain={showRain} />
          </div>
        </div>
      )}

      {/* Model Quality tab */}
      {activeTab === "model_quality" && (
        <div className={`stats-content model-quality-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                AI Quality
              </h2>
              <p className="stats-hint">GAIC aesthetic scores, crop quality metrics, and detection relevance</p>
            </div>
            <ModelQualityTab aiQualityStats={aiQualityStats} showRain={showRain} />
          </div>
        </div>
      )}

      {/* Training tab */}
      {activeTab === "training" && (
        <div className={`stats-content training-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                Face Recognition Training
              </h2>
              <p className="stats-hint">Training images for bootstrapping face recognition</p>
            </div>
            <TrainingTab trainingStats={trainingStats} showRain={showRain} />
          </div>
        </div>
      )}

      {/* System Status tab */}
      {activeTab === "system" && (
        <div className={`stats-content system-content ${showRain ? "matrix-content" : ""}`}>
          <div className="stats-section">
            <div className="section-header">
              <h2>
                <span className="section-icon">::</span>
                System Status
              </h2>
              <p className="stats-hint">Pipeline service health and system metrics</p>
            </div>
            <SystemTab systemStatus={systemStatus} showRain={showRain} />
          </div>
        </div>
      )}
    </div>
  );
}
